import { useState, useEffect, useRef } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import {
  getConsent,
  setConsent,
  hasConsentDecision,
  applyStoredConsent,
  isAuthenticated,
  hasLoadedTracking,
  OPEN_CONSENT_EVENT,
} from '@u/consent'
import { useStore } from '@/core'
import { Button } from '@c/button'
import { Checkbox } from '@/core/components/form/base/checkbox'
import styles from './style.module.less'

// App-level consent gate. Renders nothing once a decision exists, unless
// something dispatches OPEN_CONSENT_EVENT (footer / privacy-policy links) to
// let the visitor change their mind.
export const ConsentBanner = () => {
  const { t } = useTranslation()
  const store = useStore()
  const [user] = store.use(['user'])
  // 'hidden' | 'banner' | 'preferences'
  const [view, setView] = useState('hidden')
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const panelRef = useRef(null)

  useEffect(() => {
    // Re-inject whatever a prior choice permits, then show the banner only if no
    // decision has been made yet. Signed-in users are never prompted: no
    // third-party tag loads for them, so the banner would control nothing.
    applyStoredConsent()
    if (!isAuthenticated() && !hasConsentDecision()) setView('banner')

    const openPreferences = () => {
      const current = getConsent()
      setAnalytics(current ? current.analytics : false)
      setMarketing(current ? current.marketing : false)
      setView('preferences')
    }
    window.addEventListener(OPEN_CONSENT_EVENT, openPreferences)
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, openPreferences)
  }, [])

  useEffect(() => {
    if (!user?.token) return
    // A fresh document is required to stop trackers loaded before sign-in.
    if (hasLoadedTracking()) window.location.reload()
    else setView('hidden')
  }, [user?.token])

  // Move focus into the preferences panel when it opens, and let Escape close it
  // without changing the stored choice. The passive bottom banner is left alone:
  // stealing focus from the page to announce it would be more hostile than the
  // banner itself.
  useEffect(() => {
    if (view !== 'preferences') return
    const previousFocus = document.activeElement
    panelRef.current?.focus()
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      setView(hasConsentDecision() ? 'hidden' : 'banner')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [view])

  const persist = (choice) => {
    const reloadRequired = setConsent(choice)
    if (reloadRequired) {
      window.location.reload()
      return
    }
    setView('hidden')
  }

  const acceptAll = () => persist({ analytics: true, marketing: true })
  const rejectAll = () => persist({ analytics: false, marketing: false })
  const savePreferences = () => persist({ analytics, marketing })

  if (view === 'hidden') return null

  return (
    <div className={styles.overlay}>
      <div
        ref={panelRef}
        className={styles.banner}
        tabIndex={-1}
        role={view === 'preferences' ? 'dialog' : 'region'}
        aria-label={
          view === 'preferences' ? t('consent.preferencesTitle') : t('consent.title')
        }
      >
        {view === 'banner' ? (
          <>
            <div className={styles.copy}>
              <h2>{t('consent.title')}</h2>
              <p>{t('consent.description')}</p>
            </div>
            <div className={styles.actions}>
              <Button onClick={rejectAll}>
                {t('consent.rejectAll')}
              </Button>
              <Button
                variant="text"
                onClick={() => setView('preferences')}
              >
                {t('consent.managePreferences')}
              </Button>
              <Button onClick={acceptAll}>
                {t('consent.acceptAll')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.copy}>
              <h2>{t('consent.preferencesTitle')}</h2>
              <p>{t('consent.preferencesDescription')}</p>
            </div>

            <ul className={styles.categories}>
              <li className={styles.category}>
                <div className={styles.categoryText}>
                  <strong>{t('consent.categories.necessary.title')}</strong>
                  <span>{t('consent.categories.necessary.description')}</span>
                </div>
                <span className={styles.alwaysOn}>{t('consent.alwaysOn')}</span>
              </li>

              {[
                ['analytics', analytics, setAnalytics],
                ['marketing', marketing, setMarketing],
              ].map(([category, checked, setChecked]) => (
                <li key={category} className={styles.category}>
                  <label className={styles.categoryText} htmlFor={`checkbox-consent-${category}-true`}>
                    <strong id={`consent-${category}-title`}>{t(`consent.categories.${category}.title`)}</strong>
                    <span id={`consent-${category}-description`}>
                      {t(`consent.categories.${category}.description`)}
                    </span>
                  </label>
                  <div className={styles.checkbox}>
                    <Checkbox
                      name={`consent-${category}`}
                      value="true"
                      label={<span aria-hidden="true" />}
                      noBorder
                      aria-labelledby={`consent-${category}-title`}
                      aria-describedby={`consent-${category}-description`}
                      checked={checked}
                      onChange={(e) => setChecked(e.target.checked)}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className={styles.actions}>
              <Button onClick={rejectAll}>
                {t('consent.rejectAll')}
              </Button>
              <Button onClick={savePreferences}>
                {t('consent.savePreferences')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
